// Alert Config Service — Thin client for Sequence Detect configuration profiles
// Delegates WebSocket communication to DashboardService (shared, authenticated connection)

import { dashboardService } from './dashboard';
import type { ThresholdConfig } from '../components/sequenceDetect/behaviorTypes';

export interface AlertConfigProfile {
  id: string;
  userId: string;
  name: string;
  thresholds: ThresholdConfig;
  filters: Record<string, boolean>;
  activityDrop: number;
  mergeGapSec: number;
  isDefault: boolean;
  createdAt: number;
  updatedAt: number;
}

class AlertConfigService {
  /**
   * Send a request through the DashboardService's shared WebSocket connection.
   * DashboardService handles authentication, reconnection, and message routing.
   */
  private send(method: string, params?: unknown): Promise<unknown> {
    return dashboardService.send(method, params);
  }

  // ─── Alert Config CRUD ─────────────────────────────────────────────────────

  async listConfigs(): Promise<AlertConfigProfile[]> {
    try {
      const result = (await this.send('dashboard:alertConfig:list')) as any;
      if (result?.success && result.configs) {
        return result.configs.map(parseConfigProfile);
      }
      return [];
    } catch (err) {
      console.error('[AlertConfigService] Failed to list configs:', err);
      return [];
    }
  }

  async getConfig(id: string): Promise<AlertConfigProfile | null> {
    try {
      const result = (await this.send('dashboard:alertConfig:get', { id })) as any;
      if (result?.success && result.config) {
        return parseConfigProfile(result.config);
      }
      return null;
    } catch (err) {
      console.error('[AlertConfigService] Failed to get config:', err);
      return null;
    }
  }

  async getDefaultConfig(): Promise<AlertConfigProfile | null> {
    try {
      const result = (await this.send('dashboard:alertConfig:getDefault')) as any;
      if (result?.success && result.config) {
        return parseConfigProfile(result.config);
      }
      return null;
    } catch (err) {
      console.error('[AlertConfigService] Failed to get default config:', err);
      return null;
    }
  }

  async saveConfig(input: {
    id?: string;
    name: string;
    thresholds: ThresholdConfig;
    filters?: Record<string, boolean>;
    activityDrop?: number;
    mergeGapSec?: number;
    isDefault?: boolean;
  }): Promise<AlertConfigProfile | null> {
    try {
      const result = (await this.send('dashboard:alertConfig:save', input)) as any;
      if (result?.success && result.config) {
        return parseConfigProfile(result.config);
      }
      return null;
    } catch (err) {
      console.error('[AlertConfigService] Failed to save config:', err);
      return null;
    }
  }

  async deleteConfig(id: string): Promise<boolean> {
    try {
      const result = (await this.send('dashboard:alertConfig:delete', id)) as any;
      return result?.success === true;
    } catch (err) {
      console.error('[AlertConfigService] Failed to delete config:', err);
      return false;
    }
  }

  async setDefaultConfig(id: string): Promise<AlertConfigProfile | null> {
    try {
      const result = (await this.send('dashboard:alertConfig:setDefault', { id })) as any;
      if (result?.success && result.config) {
        return parseConfigProfile(result.config);
      }
      return null;
    } catch (err) {
      console.error('[AlertConfigService] Failed to set default config:', err);
      return null;
    }
  }
}

/** Parse raw DB row into a typed AlertConfigProfile */
function parseConfigProfile(raw: any): AlertConfigProfile {
  return {
    id: raw.id,
    userId: raw.user_id,
    name: raw.name,
    thresholds: raw.thresholds || (typeof raw.thresholds_json === 'string' ? JSON.parse(raw.thresholds_json) : {}),
    filters: raw.filters || (typeof raw.filters_json === 'string' ? JSON.parse(raw.filters_json) : {}),
    activityDrop: raw.activity_drop ?? 50,
    mergeGapSec: raw.merge_gap_sec ?? 5,
    isDefault: raw.is_default === 1 || raw.is_default === true,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

// Singleton instance
export const alertConfigService = new AlertConfigService();
export default alertConfigService;